import { memo, type FC } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { MatchDonutChart } from "./MatchDonutChart";

type UploadingStateProps = {
  uploadState: "UPLOADING" | "PROCESSING";
  fileName: string | null;
  totalRows: number | null;
};

export const UploadingState: FC<UploadingStateProps> = memo(
  ({ uploadState, fileName, totalRows }) => {
    const total = totalRows || 0;

    return (
      <Box>
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ borderRadius: 0 }}
        >
          <AlertTitle sx={{ mb: 0 }}>Keep this panel open</AlertTitle>
          {uploadState === "UPLOADING"
            ? "Uploading in progress..."
            : "Matching companies..."}
        </Alert>

        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              {fileName || "accounts.csv"}
            </Typography>
          </Stack>
          {uploadState === "PROCESSING" && total > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 3.5 }}
            >
              {total.toLocaleString()} rows detected
            </Typography>
          )}
        </Box>

        <Box sx={{ px: 2, py: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 2 }}
          >
            Preview of results
          </Typography>

          <Box sx={{ mb: 3 }}>
            <MatchDonutChart
              matched={0}
              unmatched={0}
              invalid={0}
              total={0}
              loading
            />
          </Box>

          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 20, color: "action.disabled" }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexGrow: 1 }}
              >
                Matches
              </Typography>
              <Skeleton width={40} height={20} animation="wave" />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1.5}>
              <HelpOutlineIcon
                sx={{ fontSize: 20, color: "action.disabled" }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexGrow: 1 }}
              >
                No match found
              </Typography>
              <Skeleton width={40} height={20} animation="wave" />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1.5}>
              <ErrorOutlineIcon
                sx={{ fontSize: 20, color: "action.disabled" }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexGrow: 1 }}
              >
                Invalid inputs
              </Typography>
              <Skeleton width={40} height={20} animation="wave" />
            </Stack>
          </Stack>
        </Box>
      </Box>
    );
  }
);

UploadingState.displayName = "UploadingState";
